import * as d3 from 'd3';
import React from 'react';
import {useQuery} from 'react-query';
import {Link, useLocation, useParams} from 'react-router-dom';
import {useQueryParam} from 'use-query-params';

import EvalPeriodSelector, {
  CustomDateRange,
  EvalPeriod,
} from 'src/components/eval/EvalPeriodSelector';
import LocationMiniMap from 'src/components/eval/LocationMiniMap';
import ModelComparisonTable, {
  LeadTimeTable,
  ModelResult,
} from 'src/components/eval/ModelComparisonTable';
import ModelStatsPanel, {ModelStats} from 'src/components/eval/ModelStatsPanel';
import LoadingSpinner from 'src/components/LoadingSpinner';
import PageContainer from 'src/components/PageContainer';
import ShortTerm from 'src/components/ShortTerm';
import TimeSeriesViz from 'src/components/TimeSeriesViz';
import {TimezoneProvider} from 'src/components/TimezoneContext';
import {MouseDateProvider} from 'src/components/Tooltip';
import {
  VizConfig,
  VizConfigCreatorItem,
  hfGetStyle,
  makeConfigItems,
} from 'src/components/Viz/config';
import {VizVisibilityProvider} from 'src/components/Viz/VisibilityContext';
import WithWidth from 'src/components/WithWidth';
import {UnitSystemProvider} from 'src/contexts/UnitSystemContext';
import {useSiteSummary} from 'src/hooks/eval/useSiteSummary';
import useForecastTimeSeries from 'src/hooks/useForecastTimeSeries';
import useObservationTimeSeries from 'src/hooks/useObservationTimeSeries';
import {CustomMetricsResponse, SiteSummary, WindowMetrics, evalApi} from 'src/lib/api/eval';
import {
  Project,
  Site as ProjectSite,
  SeriesType,
  fetchProjectWithContextOrg,
} from 'src/lib/api/project';
import {
  AllColumns,
  ApiForecastTimeSeries,
  ApiObservationTimeSeries,
  CBRFCColumns,
  CNRFCColumns,
  CompoundRFCColumns,
  FLOW_OBSERVATION_SOURCES,
  FlowForecastColumns,
  FlowObservationColumns,
  ForecastTimeSeriesQuery,
  NWRFCColumns,
  ObservationTimeSeriesQuery,
  RFCColumns,
  Source,
  UnitSystem,
  isRFCSource,
} from 'src/lib/api/series';
import COLORS, {getRFCColors} from 'src/lib/colors';
import {Dims, Margin} from 'src/lib/commonTypes';
import {EXPERIMENT_MODEL_KEYS, getModelColor, getModelDisplayName} from 'src/lib/evalModels';
import {getGraphDims} from 'src/lib/vizUtils';

type Params = {siteId: string};

const DEFAULT_EVAL_PERIOD: EvalPeriod = '90d';

function getDefaultRFCColumn(source: Source): AllColumns | null {
  if (source === Source.NWRFC) return NWRFCColumns.DISCHARGE;
  if (source === Source.RFC) return RFCColumns.FLOW;
  if (source === Source.COMPOUND_RFC) return CompoundRFCColumns.DISCHARGE;
  if (source === Source.CBRFC) return CBRFCColumns.FLOW;
  if (source === Source.CNRFC) return CNRFCColumns.DISCHARGE_DETERMINISTIC;
  return null;
}

function getOrderedModelNames(models: Record<string, unknown>): string[] {
  const names = Object.keys(models);
  const orderKey = (m: string) => {
    const key = m.toLowerCase();
    if (key === 'hydroforecast') {
      return 0;
    }
    if (key === 'nwrfc') {
      return 2;
    }
    if (key === 'persistence' || key === 'lta') {
      return 9;
    }
    return 1;
  };
  return names.sort((a, b) => orderKey(a) - orderKey(b) || a.localeCompare(b));
}

const EVAL_PERIOD_DAYS: Record<EvalPeriod, number> = {
  '10d': 10,
  '30d': 30,
  '90d': 90,
  '180d': 180,
  '365d': 365,
  all: 730,
};

function transformMetricsToStats(metrics: WindowMetrics): ModelStats {
  return {
    forecastCount: metrics.forecastCount ?? 0,
    observationCount: metrics.observationCount,
    meanObsCoverage: metrics.meanObsCoverage,
    kge: metrics.kge,
    crps: metrics.crps,
    pbias: metrics.percentBias,
    coverage80: metrics.coverage80,
    nrmse: metrics.nrmse,
    mae: metrics.mae,
  };
}

const OBSERVATION_LOOKBACK_DAYS = 10;
const FORECAST_LOOKAHEAD_DAYS = 10;

const EVAL_VIZ_MARGIN: Margin = {
  top: 10,
  right: 60,
  bottom: 20,
  left: 60,
};

function getDefaultVizDateRange(forecasts: {issueTime: Date}[]): [Date, Date] {
  const now = new Date();
  const earliestIssueTime =
    forecasts.length > 0 ? Math.min(...forecasts.map((f) => f.issueTime.getTime())) : now.getTime();

  return [
    new Date(earliestIssueTime - OBSERVATION_LOOKBACK_DAYS * 24 * 60 * 60 * 1000),
    new Date(now.getTime() + FORECAST_LOOKAHEAD_DAYS * 24 * 60 * 60 * 1000),
  ];
}

function getFitToDataVizDateRange(params: {
  forecastTimeSeries: ApiForecastTimeSeries | null;
  observationTimeSeries: ApiObservationTimeSeries | null;
}): [Date, Date] | null {
  const {forecastTimeSeries, observationTimeSeries} = params;

  const forecastMin = forecastTimeSeries?.forecasts?.length
    ? Math.min(...forecastTimeSeries.forecasts.map((f) => f.validTimeRange[0].getTime()))
    : null;
  const forecastMax = forecastTimeSeries?.forecasts?.length
    ? Math.max(...forecastTimeSeries.forecasts.map((f) => f.validTimeRange[1].getTime()))
    : null;
  const obsMin = observationTimeSeries?.dataTimeRange?.length
    ? observationTimeSeries.dataTimeRange[0].getTime()
    : null;
  const obsMax = observationTimeSeries?.dataTimeRange?.length
    ? observationTimeSeries.dataTimeRange[1].getTime()
    : null;

  const mins = [forecastMin, obsMin].filter((x): x is number => typeof x === 'number');
  const maxs = [forecastMax, obsMax].filter((x): x is number => typeof x === 'number');
  if (!mins.length || !maxs.length) return null;
  return [new Date(Math.min(...mins)), new Date(Math.max(...maxs))];
}

// ==================== VizConfig creation ====================

function buildObservationConfigItems(site: ProjectSite): VizConfigCreatorItem[] {
  const obsConfigs = site.observationTimeSeries?.[SeriesType.FlowShortTerm] ?? [];

  const hasTrend = obsConfigs.some(
    (obs) =>
      FLOW_OBSERVATION_SOURCES.includes(obs.source) &&
      obs.columns &&
      obs.columns.includes(FlowObservationColumns.observed_clean)
  );

  return obsConfigs
    .filter((q) => FLOW_OBSERVATION_SOURCES.includes(q.source) && q.columns && q.columns.length)
    .flatMap((q) => {
      const items: VizConfigCreatorItem[] = [
        {
          displayName: 'Observed',
          source: q.source,
          columns: [q.columns![0]] as AllColumns[],
          legendIcon: 'line',
          getStyle: () => ({
            stroke: COLORS.OBS,
            strokeWidth: hasTrend ? 1.5 : 2,
            strokeDasharray: hasTrend ? '6 3' : undefined,
            hfStrokeOutlineStyle: {stroke: 'white', strokeWidth: hasTrend ? 3.5 : 4},
          }),
          defaultNotVisible: hasTrend,
        },
      ];

      if (hasTrend) {
        items.push({
          displayName: 'Observed (cleaned)',
          source: q.source,
          columns: [FlowObservationColumns.observed_clean],
          legendIcon: 'line',
          getStyle: () => ({
            stroke: '#1a1a1a',
            strokeWidth: 2,
            hfStrokeOutlineStyle: {stroke: 'white', strokeWidth: 4},
          }),
        });
      }

      return items;
    });
}

function makeEvalVizConfig(params: {
  forecastTimeSeries: ApiForecastTimeSeries;
  site: ProjectSite;
}): VizConfig {
  const {forecastTimeSeries, site} = params;
  const forecastSource = forecastTimeSeries.query.source;

  const isProbabilistic =
    forecastSource === Source.HydroForecastShortTerm ||
    forecastSource === Source.HydroForecastShortTerm2 ||
    forecastSource === Source.HydroForecastShortTerm3;

  const forecasts: VizConfigCreatorItem[] = isProbabilistic
    ? [
        {
          displayName: 'Forecast (10–90%)',
          source: forecastSource,
          columns: [FlowForecastColumns.DISCHARGE_0_10, FlowForecastColumns.DISCHARGE_0_90],
          legendIcon: 'rect',
          hideTooltip: true,
          getStyle: (idx = 0, length = 0) => {
            const style = hfGetStyle(idx, length);
            return {fill: style.stroke, fillOpacity: 0.2};
          },
        },
        {
          displayName: 'Forecast (median)',
          source: forecastSource,
          columns: [FlowForecastColumns.DISCHARGE_MEDIAN],
          legendIcon: 'line',
          showForecastTimeMarkers: true,
          getStyle: (idx = 0, length = 0) => hfGetStyle(idx, length),
        },
      ]
    : [
        {
          displayName: 'Forecast',
          source: forecastSource,
          columns: forecastTimeSeries.query.columns as AllColumns[],
          legendIcon: 'line',
          showForecastTimeMarkers: true,
          getStyle: (idx = 0, length = 0) => {
            if (isRFCSource(forecastSource)) {
              const rfcColors = getRFCColors(Math.max(length, 1));
              return {stroke: rfcColors[idx] || rfcColors[0], strokeDasharray: '5'};
            }
            return hfGetStyle(idx, length);
          },
        },
      ];

  const observations = buildObservationConfigItems(site);

  return {
    forecasts: makeConfigItems('forecasts', forecasts),
    observations: makeConfigItems('observations', observations),
  };
}

// ==================== Visualization Components ====================

function EvalTimeSeriesViz({
  title,
  height,
  forecastTimeSeries,
  observationTimeSeries,
  site,
  fitToDataRange = false,
}: {
  title: string;
  height: number;
  forecastTimeSeries: ApiForecastTimeSeries;
  observationTimeSeries: ApiObservationTimeSeries;
  site: ProjectSite;
  fitToDataRange?: boolean;
}) {
  const [dateRange, setDateRange] = React.useState<[Date, Date]>(() => {
    if (fitToDataRange) {
      return (
        getFitToDataVizDateRange({forecastTimeSeries, observationTimeSeries}) ??
        getDefaultVizDateRange(forecastTimeSeries?.forecasts ?? [])
      );
    }
    return getDefaultVizDateRange(forecastTimeSeries?.forecasts ?? []);
  });

  React.useEffect(() => {
    if (fitToDataRange) {
      const next =
        getFitToDataVizDateRange({forecastTimeSeries, observationTimeSeries}) ??
        getDefaultVizDateRange(forecastTimeSeries?.forecasts ?? []);
      setDateRange(next);
      return;
    }
    setDateRange(getDefaultVizDateRange(forecastTimeSeries?.forecasts ?? []));
  }, [fitToDataRange, forecastTimeSeries, observationTimeSeries]);

  if (!forecastTimeSeries || forecastTimeSeries.forecasts.length === 0) {
    return (
      <div className="flex items-center justify-center h-[280px] text-gray-500 text-sm">
        No forecast data available for visualization.
      </div>
    );
  }

  if (!observationTimeSeries) {
    return (
      <div className="flex items-center justify-center h-[280px] text-gray-500 text-sm">
        No observation data available for visualization.
      </div>
    );
  }

  const config = makeEvalVizConfig({
    forecastTimeSeries,
    site,
  });

  return (
    <TimezoneProvider currentUser={null}>
      <VizVisibilityProvider>
        <MouseDateProvider>
          <WithWidth>
            {(width) => {
              const dims: Dims = [width, height];
              const graphDims = getGraphDims(dims, EVAL_VIZ_MARGIN);
              const x = d3
                .scaleTime()
                .domain([dateRange[0], dateRange[1]])
                .range([0, graphDims[0]]);

              return (
                <TimeSeriesViz
                  title={title}
                  config={config}
                  observationTimeSeriesList={[observationTimeSeries]}
                  forecastTimeSeriesList={[forecastTimeSeries]}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  x={x}
                  dims={dims}
                  margin={EVAL_VIZ_MARGIN}
                />
              );
            }}
          </WithWidth>
        </MouseDateProvider>
      </VizVisibilityProvider>
    </TimezoneProvider>
  );
}

// ==================== Query building from Site ====================

function getTopPlotColumns(source: Source, queryColumns?: AllColumns[]): AllColumns[] {
  if (
    source === Source.HydroForecastShortTerm ||
    source === Source.HydroForecastShortTerm2 ||
    source === Source.HydroForecastShortTerm3
  ) {
    return [FlowForecastColumns.DISCHARGE_MEDIAN];
  }

  if (queryColumns?.length) {
    const preferred: AllColumns[] = [
      NWRFCColumns.DISCHARGE,
      RFCColumns.FLOW,
      CompoundRFCColumns.DISCHARGE,
      CBRFCColumns.FLOW,
      CNRFCColumns.DISCHARGE_DETERMINISTIC,
      CNRFCColumns.DISCHARGE_ENSEMBLE,
    ];
    for (const col of preferred) {
      if (queryColumns.includes(col)) return [col];
    }
    return [queryColumns[0]];
  }

  const rfcCol = getDefaultRFCColumn(source);
  if (rfcCol) return [rfcCol];
  return [FlowForecastColumns.DISCHARGE_MEAN];
}

function findFlowObservationConfig(site: ProjectSite) {
  // Check flow-short-term first, then fall back to flow-seasonal-rolling-extended.
  // The project API is the source of truth — trust whatever source it declares.
  for (const seriesType of [SeriesType.FlowShortTerm, SeriesType.FlowSeasonalRollingExtended]) {
    const configs = site.observationTimeSeries?.[seriesType] ?? [];
    return configs.find((q) => !String(q.source).includes('historical-percentile'));
  }
  return null;
}

function buildObservationQueries(
  site: ProjectSite,
  dateRange: [Date, Date],
  unitSystem?: UnitSystem
): ObservationTimeSeriesQuery[] {
  const validConfig = findFlowObservationConfig(site);

  if (!validConfig) {
    return [];
  }

  return [
    {
      siteId: site.id,
      projectId: site.projectId,
      source: validConfig.source,
      startDate: dateRange[0],
      endDate: dateRange[1],
      columns: validConfig.columns ?? [],
      rateVolumeMode: 'rate',
      unitSystem,
    },
  ];
}

// ==================== Section Components ====================

interface HeaderSectionProps {
  siteId: string;
  displayName?: string;
  lat?: number;
  lon?: number;
  models: string[];
  organizationId?: string;
  projectId?: string;
}

function HeaderSection({
  siteId,
  displayName,
  lat,
  lon,
  models,
  organizationId,
  projectId,
}: HeaderSectionProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      {/* Mini map */}
      <div className="order-2 md:order-1">
        {lat != null && lon != null ? (
          <LocationMiniMap lat={lat} lon={lon} height={200} />
        ) : (
          <div className="h-[200px] border border-gray-200 bg-gray-50 flex items-center justify-center text-gray-500">
            Location not available
          </div>
        )}
      </div>

      {/* Metadata */}
      <div className="order-1 md:order-2">
        <h1 className="text-2xl font-semibold text-gray-900 mb-4">{displayName || siteId}</h1>
        <table className="w-full text-sm">
          <tbody>
            <tr className="border-b border-gray-200">
              <th className="py-2 pr-4 text-left text-gray-500 font-normal w-1/3">Site ID</th>
              <td className="py-2 font-medium">
                {organizationId && projectId ? (
                  <Link
                    to={`/${organizationId}/projects/${projectId}?siteId=${siteId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {siteId}
                  </Link>
                ) : (
                  siteId
                )}
              </td>
            </tr>
            <tr className="border-b border-gray-200">
              <th className="py-2 pr-4 text-left text-gray-500 font-normal">Coordinates</th>
              <td className="py-2 font-medium">
                {lat != null && lon != null
                  ? `${Math.abs(lat).toFixed(4)}°${lat >= 0 ? 'N' : 'S'}, ${Math.abs(lon).toFixed(4)}°${lon >= 0 ? 'E' : 'W'}`
                  : 'Not available'}
              </td>
            </tr>
            <tr className="border-b border-gray-200">
              <th className="py-2 pr-4 text-left text-gray-500 font-normal">Models</th>
              <td className="py-2">
                <div className="flex flex-wrap gap-2">
                  {models.map((model, i) => (
                    <span key={model} className="inline-flex items-center gap-1.5">
                      <span
                        className="w-2.5 h-2.5 rounded-full"
                        style={{backgroundColor: getModelColor(model)}}
                      />
                      <span className="font-medium">{getModelDisplayName(model)}</span>
                      {i < models.length - 1 && <span className="text-gray-400">,</span>}
                    </span>
                  ))}
                  {models.length === 0 && <span className="text-gray-400">None configured</span>}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface PerformanceEvalSectionProps {
  summary: SiteSummary;
  evalPeriod: EvalPeriod;
  onPeriodChange: (period: EvalPeriod) => void;
  customStartDate: string;
  customEndDate: string;
  onCustomStartChange: (date: string) => void;
  onCustomEndChange: (date: string) => void;
  onCustomApply: () => void;
  customWindowData?: Record<string, WindowMetrics> | null;
  customMetricsLoading?: boolean;
  site: ProjectSite;
  organizationId?: string;
  unitSystem?: UnitSystem;
}

function getActiveCustomDateRange(
  customWindowData: Record<string, WindowMetrics> | null | undefined,
  customStartDate: string,
  customEndDate: string
): [Date, Date] | null {
  if (!customWindowData || !customStartDate || !customEndDate) return null;
  return [new Date(customStartDate), new Date(customEndDate)];
}

function PerformanceEvalSection({
  summary,
  evalPeriod,
  onPeriodChange,
  customStartDate,
  customEndDate,
  onCustomStartChange,
  onCustomEndChange,
  onCustomApply,
  customWindowData,
  customMetricsLoading,
  site,
  organizationId,
  unitSystem,
}: PerformanceEvalSectionProps) {
  const customDateRange = getActiveCustomDateRange(
    customWindowData,
    customStartDate,
    customEndDate
  );
  const BASELINE_MODELS = new Set(['persistence', 'lta']);
  const allModelNames = getOrderedModelNames(summary.models);
  const modelNames = allModelNames.filter(
    (m) => !BASELINE_MODELS.has(m.toLowerCase()) && !EXPERIMENT_MODEL_KEYS.has(m.toLowerCase())
  );

  const getWindowData = (model: string): WindowMetrics | undefined => {
    if (customWindowData?.[model]) return customWindowData[model];
    const modelData = summary.models[model];
    // Prefer config-level short-term window over top-level window, which
    // pools all configs (including seasonal) and can skew short-term metrics.
    const shortTermWindow = modelData?.configs?.['short-term']?.windows?.[evalPeriod];
    return shortTermWindow ?? modelData?.windows?.[evalPeriod];
  };

  // Extract baseline metrics for skill indicators.
  const persistenceWindow = getWindowData('persistence');
  const ltaWindow = getWindowData('lta');
  const persistenceKge = persistenceWindow?.kge?.mean;
  const persistenceNrmse = persistenceWindow?.nrmse?.mean;
  const persistenceMae = persistenceWindow?.mae?.mean;
  const ltaKge = ltaWindow?.kge?.mean;
  const ltaNrmse = ltaWindow?.nrmse?.mean;
  const ltaMae = ltaWindow?.mae?.mean;

  const nonBaselineResults: ModelResult[] = modelNames
    .map((model) => {
      const windowData = getWindowData(model);
      if (!windowData) return null;

      return {
        model,
        metrics: {
          kge: windowData.kge?.mean,
          crps: windowData.crps?.mean,
          nrmse: windowData.nrmse?.mean,
          mae: windowData.mae?.mean,
          coverage80: windowData.coverage80?.mean,
          percentBias: windowData.percentBias?.mean,
          kgePersistence: persistenceKge,
          kgeLta: ltaKge,
          nrmsePersistence: persistenceNrmse,
          nrmseLta: ltaNrmse,
          maePersistence: persistenceMae,
          maeLta: ltaMae,
        },
      } as ModelResult;
    })
    .filter((r): r is ModelResult => r !== null);

  const baselineResults: ModelResult[] = ['persistence', 'lta']
    .map((model) => {
      const windowData = getWindowData(model);
      if (!windowData) return null;
      return {
        model,
        isBaseline: true,
        metrics: {
          kge: windowData.kge?.mean,
          crps: windowData.crps?.mean,
          nrmse: windowData.nrmse?.mean,
          mae: windowData.mae?.mean,
          percentBias: windowData.percentBias?.mean,
        },
      } as ModelResult;
    })
    .filter((r): r is ModelResult => r !== null);

  const modelResults = [...nonBaselineResults, ...baselineResults];

  return (
    <section>
      <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
        Performance Evaluation
      </h2>

      <div className="space-y-3 mb-6">
        <EvalPeriodSelector value={evalPeriod} onChange={onPeriodChange} />
        <CustomDateRange
          startDate={customStartDate}
          endDate={customEndDate}
          onStartChange={onCustomStartChange}
          onEndChange={onCustomEndChange}
          onApply={onCustomApply}
        />
      </div>

      {customMetricsLoading && (
        <div className="py-4 text-center text-gray-500 text-sm">Loading custom metrics...</div>
      )}

      {modelResults.length > 0 && (
        <div className="mb-6">
          <ModelComparisonTable results={modelResults} />
          <LeadTimeTable results={modelResults} />
        </div>
      )}

      <div className="space-y-6">
        {modelNames.map((model) => {
          const windowData = getWindowData(model);
          if (!windowData) return null;

          const stats = {
            ...transformMetricsToStats(windowData),
            kgePersistence: persistenceKge,
            kgeLta: ltaKge,
            nrmsePersistence: persistenceNrmse,
            nrmseLta: ltaNrmse,
            maePersistence: persistenceMae,
            maeLta: ltaMae,
          };
          return (
            <ModelEvalSection
              key={model}
              model={model}
              stats={stats}
              site={site}
              evalPeriod={evalPeriod}
              customDateRange={customDateRange}
              organizationId={organizationId}
              unitSystem={unitSystem}
            />
          );
        })}

        {modelNames.length === 0 && (
          <div className="border border-gray-200 bg-white p-4 text-gray-500 text-center">
            No evaluation data available for this site.
          </div>
        )}
      </div>
    </section>
  );
}

interface ModelEvalSectionProps {
  model: string;
  stats: ModelStats;
  site: ProjectSite;
  evalPeriod: EvalPeriod;
  customDateRange?: [Date, Date] | null;
  organizationId?: string;
  unitSystem?: UnitSystem;
}

function ModelEvalSection({
  model,
  stats,
  site,
  evalPeriod,
  customDateRange,
  organizationId,
  unitSystem,
}: ModelEvalSectionProps) {
  const periodDays = EVAL_PERIOD_DAYS[evalPeriod];

  // Use custom date range when active, otherwise fall back to period-based range
  const dateRange = React.useMemo((): [Date, Date] => {
    if (customDateRange) return customDateRange;
    const now = new Date();
    return [new Date(now.getTime() - periodDays * 24 * 60 * 60 * 1000), now];
  }, [customDateRange, periodDays]);

  // Find the forecast source for this eval model
  const forecastSource = React.useMemo(() => {
    const key = model.toLowerCase();
    if (key === 'hydroforecast') return Source.HydroForecastShortTerm;
    if (key === 'nwrfc') return Source.NWRFC;
    if (key === 'rfc') return Source.RFC;
    if (key === 'compound-rfc') return Source.COMPOUND_RFC;
    if (key === 'cbrfc') return Source.CBRFC;
    if (key === 'cnrfc') return Source.CNRFC;
    return null;
  }, [model]);

  // Build queries using the site configuration
  const forecastQueries: ForecastTimeSeriesQuery[] = React.useMemo(() => {
    if (!forecastSource) return [];

    const siteConfig = site.forecastTimeSeries?.[SeriesType.FlowShortTerm]?.find(
      (q) => q.source === forecastSource
    );

    // Determine columns based on model type, preferring the project config
    const columns: AllColumns[] = isRFCSource(forecastSource)
      ? getTopPlotColumns(forecastSource, siteConfig?.columns)
      : [
          FlowForecastColumns.DISCHARGE_0_10,
          FlowForecastColumns.DISCHARGE_MEDIAN,
          FlowForecastColumns.DISCHARGE_0_90,
        ];

    return [
      {
        siteId: site.id,
        projectId: siteConfig?.projectId ?? site.projectId,
        source: forecastSource,
        initializationTimeStartDate: dateRange[0],
        initializationTimeEndDate: dateRange[1],
        columns,
        unitSystem,
      },
    ];
  }, [forecastSource, site, dateRange, unitSystem]);

  const observationQueries = React.useMemo(
    () => buildObservationQueries(site, dateRange, unitSystem),
    [site, dateRange, unitSystem]
  );

  const forecastResult = useForecastTimeSeries(forecastQueries, {
    contextOrgId: organizationId,
  });
  const observationResult = useObservationTimeSeries(observationQueries, {
    contextOrgId: organizationId,
  });

  const isLoading = forecastResult.isLoading || observationResult.isLoading;

  // Sort forecasts by issue time, sampling for long windows to avoid perf issues
  // <30d: every forecast, 30-90d: every other, 90d+: every fourth
  const forecastTimeSeries = React.useMemo(() => {
    const ts = forecastResult.data?.[0];
    if (!ts) return null;
    let forecasts = [...(ts.forecasts ?? [])].sort(
      (a, b) => a.issueTime.getTime() - b.issueTime.getTime()
    );
    const spanDays = customDateRange
      ? (customDateRange[1].getTime() - customDateRange[0].getTime()) / (24 * 60 * 60 * 1000)
      : periodDays;
    const step = spanDays >= 90 ? 4 : spanDays >= 30 ? 2 : 1;
    if (step > 1) {
      forecasts = forecasts.filter((_, i) => i % step === 0);
    }
    return {...ts, forecasts};
  }, [forecastResult.data, customDateRange, periodDays]);

  const observationTimeSeries = observationResult.data?.[0] ?? null;

  return (
    <div className="border border-gray-200 bg-white">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{backgroundColor: getModelColor(model)}}
          />
          {getModelDisplayName(model)}
        </h3>
      </div>

      <div className="p-4 border-b border-gray-200">
        <ModelStatsPanel stats={stats} />
        {stats.forecastCount === 0 && (
          <div className="mt-2 text-sm text-gray-500">
            No evaluation statistics yet for this model (still processing).
          </div>
        )}
      </div>

      <div className="p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-[250px]">
            <LoadingSpinner />
          </div>
        ) : forecastTimeSeries?.forecasts?.length && observationTimeSeries ? (
          <EvalTimeSeriesViz
            title={getModelDisplayName(model)}
            height={250}
            forecastTimeSeries={forecastTimeSeries}
            observationTimeSeries={observationTimeSeries}
            site={site}
            fitToDataRange={true}
          />
        ) : (
          <div className="flex items-center justify-center h-[250px] text-gray-500 text-sm">
            No forecasts available for this model in selected period.
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== Main Page Component ====================

export default function EvalSiteDetail() {
  const {siteId} = useParams() as Params;
  const {pathname} = useLocation();

  const [evalPeriodParam, setEvalPeriodParam] = useQueryParam<EvalPeriod | null>('window');
  const evalPeriod = evalPeriodParam || DEFAULT_EVAL_PERIOD;

  const [customStartDate, setCustomStartDate] = React.useState('');
  const [customEndDate, setCustomEndDate] = React.useState('');

  const {summary, isLoaded} = useSiteSummary(siteId);

  const hasSeasonal = summary
    ? Object.values(summary.models).some((m) => m.configs?.seasonal)
    : false;

  // Fetch project to get the Site configuration
  const {data: project} = useQuery(
    ['eval', 'site', siteId, 'project', summary?.organizationId, summary?.projectId],
    async () => {
      if (!summary?.organizationId || !summary?.projectId) return null;
      return await fetchProjectWithContextOrg(summary.projectId, summary.organizationId);
    },
    {
      enabled: !!summary?.organizationId && !!summary?.projectId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
    }
  );

  const projectSite: ProjectSite | null = project?.sites?.find((s) => s.id === siteId) ?? null;

  const BASELINE_MODELS = new Set(['persistence', 'lta']);
  const modelNames = summary
    ? Object.keys(summary.models).filter(
        (m) => !BASELINE_MODELS.has(m.toLowerCase()) && !EXPERIMENT_MODEL_KEYS.has(m.toLowerCase())
      )
    : [];
  const displayName = summary?.displayName;
  const lat = summary?.lat;
  const lon = summary?.lon;

  const [customWindowData, setCustomWindowData] = React.useState<Record<
    string,
    WindowMetrics
  > | null>(null);
  const [customMetricsLoading, setCustomMetricsLoading] = React.useState(false);

  const handleCustomApply = async () => {
    if (!customStartDate || !customEndDate || !summary) {
      return;
    }
    setCustomMetricsLoading(true);
    try {
      const results: Record<string, WindowMetrics> = {};
      for (const model of Object.keys(summary.models)) {
        const resp = await evalApi.getCustomMetrics(siteId, {
          start: customStartDate,
          end: customEndDate,
          model,
        });
        const m = resp.metrics;
        const n = (v: number | null | undefined) => v ?? NaN;
        results[model] = {
          forecastCount: resp.forecastCount,
          observationCount: resp.observationCount,
          meanObsCoverage: resp.meanObsCoverage ?? undefined,
          kge: {mean: n(m.kge)},
          crps: {
            mean: n(m.crps?.mean),
            p10: m.crps?.p10 ?? undefined,
            p90: m.crps?.p90 ?? undefined,
          },
          nrmse: {mean: n(m.nrmse)},
          coverage80: {
            mean: n(m.coverage80?.mean),
            p10: m.coverage80?.p10 ?? undefined,
            p90: m.coverage80?.p90 ?? undefined,
          },
          mae: {mean: n(m.mae)},
          percentBias: {mean: n(m.percentBias)},
        };
      }
      setCustomWindowData(results);
    } catch (err) {
      console.error('Failed to load custom metrics:', err);
      setCustomWindowData(null);
    } finally {
      setCustomMetricsLoading(false);
    }
  };

  return (
    <PageContainer>
      <div className="mb-4">
        <Link to="/eval/short-term" className="text-blue-600 hover:text-blue-800 text-sm">
          &larr; Back to map
        </Link>
      </div>

      {hasSeasonal && (
        <div className="flex gap-1 mb-4 border-b border-gray-200">
          <span className="px-3 py-2 text-sm font-medium border-b-2 border-blue-500 text-blue-600 -mb-px">
            Short-Term (10d)
          </span>
          <Link
            to={`${pathname}?horizon=seasonal`}
            className="px-3 py-2 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 -mb-px"
          >
            Seasonal (365d)
          </Link>
        </div>
      )}

      {!isLoaded ? (
        <div className="py-6">
          <LoadingSpinner />
        </div>
      ) : !summary || !projectSite ? (
        <div className="py-6 text-center text-gray-500">Loading site configuration...</div>
      ) : (
        <>
          <HeaderSection
            siteId={siteId}
            displayName={displayName || siteId}
            lat={lat}
            lon={lon}
            models={modelNames}
            organizationId={summary.organizationId}
            projectId={summary.projectId}
          />

          <TimezoneProvider currentUser={null}>
            <UnitSystemProvider unitSystem={project!.unitSystem}>
              <ShortTerm
                site={projectSite}
                includeFlowContributingAreas={false}
                setIncludeFlowContributingAreas={() => {}}
                project={project as Project}
                flowContributingAreas={null}
              />
            </UnitSystemProvider>
          </TimezoneProvider>

          <PerformanceEvalSection
            summary={summary}
            evalPeriod={evalPeriod}
            onPeriodChange={(p) => {
              setEvalPeriodParam(p);
              setCustomWindowData(null);
            }}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            onCustomStartChange={setCustomStartDate}
            onCustomEndChange={setCustomEndDate}
            onCustomApply={handleCustomApply}
            customWindowData={customWindowData}
            customMetricsLoading={customMetricsLoading}
            site={projectSite}
            organizationId={summary.organizationId}
            unitSystem={project?.unitSystem}
          />
        </>
      )}
    </PageContainer>
  );
}
